import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import SeoJsonLd from "@/components/SeoJsonLd";
import {
  buildBlogPostingSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildOrganizationSchema,
  buildPageSchemaGraph,
} from "@/lib/seoSchemas";

const OG_IMAGE = "https://getblackride.com/logo.png";
const WHATSAPP_NUMBER = "966540423401";

function ensureMeta(selector, identity, content) {
  let node = document.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    Object.entries(identity).forEach(([key, value]) => node.setAttribute(key, value));
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
}

function buildWhatsappLink(customMessage, title) {
  const message = encodeURIComponent(
    customMessage ||
      `Hello BlackRide, I would like help with ${title}. Please guide me on the best vehicle class, availability, and pricing.`,
  );
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
}

export default function BlogArticlePage({
  title,
  description,
  canonical,
  datePublished,
  updatedAt,
  intro,
  directAnswer,
  sections,
  serviceLinks,
  relatedArticles,
  faqs = [],
  ctaLabel = "Book on WhatsApp",
  author = "BlackRide Editorial Team",
  breadcrumbs = [],
  tags = [],
  whatsappMessage,
  metaTitle,
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
}) {
  const whatsappHref = useMemo(() => buildWhatsappLink(whatsappMessage, title), [title, whatsappMessage]);
  const tableOfContents = useMemo(() => sections.map((section) => section.title), [sections]);
  const resolvedMetaTitle = metaTitle || `${title} | BlackRide`;
  const resolvedOgTitle = ogTitle || resolvedMetaTitle;
  const resolvedOgDescription = ogDescription || description;
  const resolvedTwitterTitle = twitterTitle || resolvedOgTitle;
  const resolvedTwitterDescription = twitterDescription || resolvedOgDescription;
  const schemaGraph = useMemo(
    () =>
      buildPageSchemaGraph([
        buildBlogPostingSchema({
          headline: title,
          description,
          url: canonical,
          image: OG_IMAGE,
          datePublished,
          dateModified: updatedAt,
          authorName: author,
          keywords: tags,
        }),
        buildOrganizationSchema(),
        buildBreadcrumbSchema(breadcrumbs),
        buildFaqSchema(faqs),
      ]),
    [author, breadcrumbs, canonical, datePublished, description, faqs, tags, title, updatedAt],
  );

  useEffect(() => {
    document.title = resolvedMetaTitle;
    ensureMeta('meta[name="description"]', { name: "description" }, description);
    ensureMeta('meta[property="og:title"]', { property: "og:title" }, resolvedOgTitle);
    ensureMeta('meta[property="og:description"]', { property: "og:description" }, resolvedOgDescription);
    ensureMeta('meta[property="og:url"]', { property: "og:url" }, canonical);
    ensureMeta('meta[property="og:type"]', { property: "og:type" }, "article");
    ensureMeta('meta[property="og:image"]', { property: "og:image" }, OG_IMAGE);
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image");
    ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" }, resolvedTwitterTitle);
    ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" }, resolvedTwitterDescription);
    ensureMeta('meta[name="twitter:image"]', { name: "twitter:image" }, OG_IMAGE);

    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute("href", canonical);
  }, [
    canonical,
    description,
    resolvedMetaTitle,
    resolvedOgDescription,
    resolvedOgTitle,
    resolvedTwitterDescription,
    resolvedTwitterTitle,
  ]);

  const pageClasses = "bg-[#050505] text-white";
  const mutedText = "text-gray-300";
  const cardClasses = "border-white/10 bg-[#111]";
  const nestedCardClasses = "border-white/10 bg-black/20";
  const headingText = "text-white";
  const tocLink = "text-gray-300 hover:text-white";
  const tagClasses = "border-white/10 text-gray-300";
  const subtleDivider = "border-white/10";
  const heroAnswer = "border-white/10 bg-[#111]";
  const linkCardHover = "hover:border-[#C7A239]/35";

  return (
    <div className={`min-h-screen px-6 py-16 md:px-12 pt-24 md:pt-28 ${pageClasses}`}>
      <SeoJsonLd id="blog-post-schema" data={schemaGraph} />

      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex flex-wrap gap-2 text-xs uppercase tracking-[0.2em] text-white/45">
          {breadcrumbs.map((item, index) => (
            <span key={item.item} className="inline-flex items-center gap-2">
              {index === breadcrumbs.length - 1 ? (
                <span className="text-[#C7A239]">{item.name}</span>
              ) : (
                <Link to={item.item} className="hover:text-white transition">
                  {item.name}
                </Link>
              )}
              {index < breadcrumbs.length - 1 ? <span>/</span> : null}
            </span>
          ))}
        </div>

        <p className="text-sm text-[#C7A239] mb-4">
          {datePublished} · {author}
        </p>
        <h1 className={`font-['Playfair_Display'] text-4xl md:text-5xl font-semibold mb-6 ${headingText}`}>{title}</h1>
        <p className={`text-lg leading-8 mb-6 ${mutedText}`}>{intro}</p>

        {directAnswer ? (
          <div className={`rounded-2xl border p-6 mb-10 ${heroAnswer}`}>
            <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-3">Direct answer</p>
            <p className="leading-7 text-gray-200">{directAnswer}</p>
          </div>
        ) : null}

        <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
          <div className={`space-y-8 leading-8 ${mutedText}`}>
            {sections.map((section) => (
              <section key={section.title} id={section.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>
                <h2 className={`text-2xl md:text-3xl font-semibold mb-3 ${headingText}`}>{section.title}</h2>
                <div className="space-y-4">
                  {section.paragraphs.map((paragraph, index) => (
                    <p key={typeof paragraph === "string" ? paragraph : `${section.title}-${index}`}>
                      {typeof paragraph === "string" ? paragraph : paragraph.text}
                    </p>
                  ))}
                </div>
                {section.links?.length ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {section.links.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={`block rounded-2xl border p-4 transition ${cardClasses} ${linkCardHover}`}
                      >
                        <p className={`text-sm font-semibold mb-1 ${headingText}`}>{item.title}</p>
                        <p className="text-sm leading-6 text-gray-400">{item.description}</p>
                      </Link>
                    ))}
                  </div>
                ) : null}
                {section.cta ? (
                  <div className={`mt-5 rounded-2xl border p-5 ${heroAnswer}`}>
                    <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-2">
                      {section.cta.eyebrow || "Plan with BlackRide"}
                    </p>
                    <h3 className={`text-xl font-semibold mb-2 ${headingText}`}>{section.cta.title}</h3>
                    <p className={`leading-7 mb-4 ${mutedText}`}>{section.cta.description}</p>
                    <a
                      href={buildWhatsappLink(section.cta.message || whatsappMessage, title)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 bg-[#C7A239] text-black px-5 py-3 rounded-full font-medium hover:opacity-90 transition"
                    >
                      <MessageCircle className="w-4 h-4" />
                      {section.cta.label || ctaLabel}
                    </a>
                  </div>
                ) : null}
              </section>
            ))}
          </div>

          <aside className={`lg:sticky lg:top-28 h-fit rounded-3xl border p-6 ${cardClasses}`}>
            <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-4">On this page</p>
            <div className="space-y-3">
              {tableOfContents.map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`block text-sm transition ${tocLink}`}
                >
                  {item}
                </a>
              ))}
            </div>

            {tags.length ? (
              <div className={`mt-6 pt-6 border-t ${subtleDivider}`}>
                <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-3">Topics</p>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span key={tag} className={`rounded-full border px-3 py-1 text-xs ${tagClasses}`}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </aside>
        </div>

        <section className={`mt-12 rounded-3xl border p-8 ${cardClasses}`}>
          <div className="flex items-start justify-between gap-6 flex-col md:flex-row md:items-center mb-6">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-2">Need a premium transfer?</p>
              <h2 className={`font-['Playfair_Display'] text-3xl ${headingText}`}>Talk to BlackRide on WhatsApp</h2>
            </div>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#C7A239] text-black px-5 py-3 rounded-full font-medium hover:opacity-90 transition"
            >
              <MessageCircle className="w-4 h-4" />
              {ctaLabel}
            </a>
          </div>
          <p className={`leading-7 ${mutedText}`}>
            Share your route, date, time, passenger count, luggage details, and preferred vehicle class. BlackRide
            can recommend the right chauffeur option and confirm a tailored quote on WhatsApp.
          </p>
        </section>

        {faqs.length > 0 && (
          <section className={`mt-12 rounded-3xl border p-8 ${cardClasses}`}>
            <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-3">FAQ</p>
            <h2 className={`font-['Playfair_Display'] text-3xl mb-6 ${headingText}`}>Frequently asked questions</h2>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.question} className={`rounded-2xl border p-5 ${nestedCardClasses}`}>
                  <h3 className={`text-xl font-semibold mb-2 ${headingText}`}>{faq.question}</h3>
                  <p className={`leading-7 ${mutedText}`}>{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12 grid gap-6 lg:grid-cols-2">
          <div className={`rounded-3xl border p-7 ${cardClasses}`}>
            <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-3">Relevant service pages</p>
            <div className="space-y-4">
              {serviceLinks.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`block rounded-2xl border p-4 transition ${nestedCardClasses} ${linkCardHover}`}
                >
                  <h3 className={`text-lg font-semibold mb-2 ${headingText}`}>{item.title}</h3>
                  <p className="text-sm leading-6 text-gray-400">{item.description}</p>
                </Link>
              ))}
            </div>
          </div>

          <div className={`rounded-3xl border p-7 ${cardClasses}`}>
            <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-3">Related guides</p>
            <div className="space-y-4">
              {relatedArticles.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`block rounded-2xl border p-4 transition ${nestedCardClasses} ${linkCardHover}`}
                >
                  <h3 className={`text-lg font-semibold mb-2 ${headingText}`}>{item.title}</h3>
                  <p className="text-sm leading-6 text-gray-400">{item.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <div className="mt-12 flex gap-4 flex-wrap">
          <Link
            to="/blog"
            className="inline-block border border-white/20 text-white px-5 py-3 rounded-full font-medium hover:bg-white hover:text-black transition"
          >
            Back to Blog
          </Link>
          <Link
            to="/"
            className="inline-block bg-[#C7A239] text-black px-5 py-3 rounded-full font-medium hover:opacity-90 transition"
          >
            Back to Home
          </Link>
        </div>
      </div>

      <div className="fixed bottom-4 left-4 right-4 z-30 md:hidden">
        <a
          href={whatsappHref}
          target="_blank"
          rel="noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#C7A239] text-black px-5 py-3 rounded-full font-semibold shadow-[0_18px_38px_rgba(0,0,0,0.35)]"
        >
          <MessageCircle className="w-4 h-4" />
          {ctaLabel}
        </a>
      </div>
    </div>
  );
}
